import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { AddSectionButton, AddSectionOverlay, toneStyles } from '@/components/block-primitives';
import type { FunnelBlockConfig, FunnelFormat, FunnelStage } from './artifact-block-types';
import EditableText from './editable-text';

// percent treats the value as a fraction: 0.83 → "83%".
const formatValue = (value: number, format?: FunnelFormat) => {
  if (format === 'integer') return Math.round(value).toLocaleString('en-US');
  if (format === 'percent') return `${(value * 100).toFixed(1).replace(/\.0$/, '')}%`;
  return String(value);
};
type ArtifactFunnelBlockProps = {
  config: FunnelBlockConfig;
  editable?: boolean;
  onChange?: (next: FunnelBlockConfig) => void;
};
const ArtifactFunnelBlock = t0 => {
  const $ = _c(48);
  const {
    config,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const [addTitle, setAddTitle] = useState(false);
  const [addNote, setAddNote] = useState(false);
  let t2;
  if ($[0] !== config || $[1] !== onChange) {
    t2 = next => onChange?.({
      ...config,
      ...next
    });
    $[0] = config;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const patch = t2;
  let t3;
  if ($[3] !== config.stages || $[4] !== patch) {
    t3 = (index, next_0) => patch({
      stages: config.stages.map((stage, i) => i === index ? {
        ...stage,
        ...next_0
      } : stage)
    });
    $[3] = config.stages;
    $[4] = patch;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const patchStage = t3;
  const max = config.stages.reduce(_temp, 0);
  const showTitle = !!config.title || addTitle;
  const showNote = !!config.note || addNote;
  let t4;
  if ($[6] !== editable || $[7] !== showTitle) {
    t4 = editable && !showTitle && <AddSectionOverlay position="top"><AddSectionButton label="Add a title" onClick={() => setAddTitle(true)} /></AddSectionOverlay>;
    $[6] = editable;
    $[7] = showTitle;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== editable || $[10] !== showNote) {
    t5 = editable && !showNote && <AddSectionOverlay position="bottom"><AddSectionButton label="Add a note" onClick={() => setAddNote(true)} /></AddSectionOverlay>;
    $[9] = editable;
    $[10] = showNote;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = config.title || "Funnel";
  let t7;
  if ($[12] !== addTitle || $[13] !== config.title || $[14] !== editable || $[15] !== patch || $[16] !== showTitle) {
    t7 = showTitle && <EditableText autoFocus={addTitle} block={true} className="text-label-2 text-primary" editable={editable} label="Funnel title" onSubmit={title => patch({
      title
    })} onEmptyBlur={() => setAddTitle(false)} optional={true} placeholder="Add a title" value={config.title ?? ""} />;
    $[12] = addTitle;
    $[13] = config.title;
    $[14] = editable;
    $[15] = patch;
    $[16] = showTitle;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== config.format || $[19] !== config.stages || $[20] !== editable || $[21] !== max || $[22] !== patchStage) {
    let t9;
    if ($[24] !== config.format || $[25] !== editable || $[26] !== max || $[27] !== patchStage) {
      t9 = (stage_1, index_0) => {
        const width = stage_1.value > 0 && max > 0 ? Math.max(3, stage_1.value / max * 100) : 0;
        return <div key={index_0}><div className="flex items-baseline justify-between gap-4"><EditableText className="min-w-0 break-words text-body-2 text-primary" editable={editable} label="Stage label" onSubmit={label => patchStage(index_0, {
              label
            })} value={stage_1.label} /><span className="shrink-0 text-label-2 tabular-nums text-primary">{formatValue(stage_1.value, config.format)}</span></div><div aria-hidden="true" className="mt-1.5 h-2 overflow-hidden rounded-full bg-senary"><div className={cx("h-full rounded-full", toneStyles[stage_1.tone ?? "neutral"].marker)} style={{
              width: `${width}%`
            }} /></div>{(editable || stage_1.detail) && <EditableText as="div" block={true} className="mt-2 break-words text-caption-4 text-tertiary" editable={editable} label="Stage detail" onSubmit={detail => patchStage(index_0, {
            detail
          })} optional={true} placeholder="Add detail" value={stage_1.detail ?? ""} />}</div>;
      };
      $[24] = config.format;
      $[25] = editable;
      $[26] = max;
      $[27] = patchStage;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    t8 = config.stages.map(t9);
    $[18] = config.format;
    $[19] = config.stages;
    $[20] = editable;
    $[21] = max;
    $[22] = patchStage;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[29] !== t8) {
    t9 = <div className="flex flex-col gap-5">{t8}</div>;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t7 || $[32] !== t9) {
    t10 = <div className="flex flex-col gap-4 px-4 py-4 md:px-6 md:py-5">{t7}{t9}</div>;
    $[31] = t7;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== addNote || $[35] !== config.note || $[36] !== editable || $[37] !== patch || $[38] !== showNote) {
    t11 = showNote && <div className="border-t border-tertiary bg-secondary px-4 md:px-6 py-3 text-caption-4 text-tertiary"><EditableText autoFocus={addNote} block={true} editable={editable} label="Funnel note" onSubmit={note => patch({
        note
      })} onEmptyBlur={() => setAddNote(false)} optional={true} placeholder="Add a note" value={config.note ?? ""} /></div>;
    $[34] = addNote;
    $[35] = config.note;
    $[36] = editable;
    $[37] = patch;
    $[38] = showNote;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== t10 || $[41] !== t11 || $[42] !== t6) {
    t12 = <section aria-label={t6} className="overflow-hidden rounded-lg border border-tertiary bg-primary">{t10}{t11}</section>;
    $[40] = t10;
    $[41] = t11;
    $[42] = t6;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== t12 || $[45] !== t4 || $[46] !== t5) {
    t13 = <div className="group relative">{t4}{t5}{t12}</div>;
    $[44] = t12;
    $[45] = t4;
    $[46] = t5;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  return t13;
};
export default ArtifactFunnelBlock;
function _temp(peak, stage_0) {
  return Math.max(peak, stage_0.value);
}